import clsx from "clsx";
import get from "lodash/get";
import React, { useEffect, useState } from "react";
import { UNDEFINED } from "react-hook-form/dist/constants";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import Button from "~/shared/ui/Fields/Button";
import DatePicker from "~/shared/ui/Fields/DatePicker";
import Select from "~/shared/ui/Fields/Select";
import { DEALER_PASSPORT_OPTION_STATUS } from "~/components/Forms/DealerPassportForm/options";
import { useSetLocationData } from "~/hooks/locationData";
import {
  DealersLocationItem,
  useGetDealersLocationItemQuery,
  ViolationTypeCategory,
} from "~/schemas/generated/graphql";
import { FilterExistenceType } from "~/schemas/generated/graphql";
import { filterItem } from "~/shared/lib/filterHelpers";
import { Input } from "~/shared/ui/Fields/Input";

import s from "./DealersFilter.less";

type IFilter = {
  FO: string;
  city: string;
  country: string;
  subject: string;
};

const DealersFilter = ({
  toggleModal,
  refetch,
  dealersForSelect,
  filterSettings,
  setFilterSettings,
  resetFilter,
}) => {
  const language = localStorage.getItem("language");
  const isEn = language === "en";
  const { t } = useTranslation();

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      ...filterSettings,
      ContractEndDate:
        filterSettings.ContractEndDate &&
        new Date(filterSettings.ContractEndDate),
    },
  });

  const [controlLocationItem, setControlLocationItem] = useState<
    Partial<IFilter>
  >({
    city: undefined,
    FO: undefined,
    subject: undefined,
    country: undefined,
  });

  const diealerCity = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["City"],
      // ...controlLocationItem,
      ...filterItem(controlLocationItem, (elem) => elem !== "city"),
    },
  });

  const diealerCountry = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["Country"],
      // ...controlLocationItem,
      ...filterItem(controlLocationItem, (elem) => elem !== "country"),
    },
  });

  const diealerSubject = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["Subject"],
      // ...controlLocationItem,
      ...filterItem(controlLocationItem, (elem) => elem !== "subject"),
    },
  });

  const diealerFo = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["Fo"],
      // ...controlLocationItem,
      ...filterItem(controlLocationItem, (elem) => elem !== "FO"),
    },
  });

  const selectCity = get(
    diealerCity,
    `data.DealerGetter.getDealersLocationItem.${isEn ? "itemsEn" : "items"}`,
    []
  );
  const selectCountry = get(
    diealerCountry,
    `data.DealerGetter.getDealersLocationItem.${isEn ? "itemsEn" : "items"}`,
    []
  );
  const selectSubject = get(
    diealerSubject,
    `data.DealerGetter.getDealersLocationItem.${isEn ? "itemsEn" : "items"}`,
    []
  );
  const selectFo = get(
    diealerFo,
    `data.DealerGetter.getDealersLocationItem.${isEn ? "itemsEn" : "items"}`,
    []
  );

  const optionLocationItem = (name) => {
    switch (name) {
      case "FO":
        return ["common.not_selected", ...selectFo];
      case "subject":
        return ["common.not_selected", ...selectSubject];
      case "country":
        return ["common.not_selected", ...selectCountry];
      case "city":
        return ["common.not_selected", ...selectCity];
    }
  };

  const FO = watch("FO");
  const city = watch("city");
  const country = watch("country");
  const subject = watch("subject");

  const filters = {
    FO: typeof FO !== "object" ? FO : !FO?.value ? undefined : FO?.value,
    city:
      typeof city !== "object" ? city : !city?.value ? undefined : city?.value,
    country:
      typeof country !== "object"
        ? country
        : !country?.value
        ? undefined
        : country?.value,
    subject:
      typeof subject !== "object"
        ? subject
        : !subject?.value
        ? undefined
        : subject?.value,
  } as IFilter;

  useEffect(() => {
    if (JSON.stringify(filters) !== JSON.stringify(controlLocationItem)) {
      setControlLocationItem(filters);
    }
  }, [JSON.stringify(filters)]);

  const cancelForm = () => {
    resetFilter();
    refetch();
    toggleModal();
  };
  const onSubmit = (data) => {
    setFilterSettings(data);
    toggleModal();
  };

  return (
    <form className={s.filterWrapper} onSubmit={handleSubmit(onSubmit)}>
      <ul className={s.filterControls}>
        <li className={s.filterControls_item}>
          <label>{t("dealers.id")}</label>
          <Input
            key={`id`}
            control={control}
            name={"id"}
            orange
            customClassName={s.item__little}
          />
        </li>
        <li className={s.filterControls_item}>
          <label>{t("dealers.DealerName")}</label>
          <Input
            key={`$DealerName`}
            control={control}
            name={"DealerName"}
            orange
            customClassName={s.item__little}
          />
        </li>

        <li className={s.filterControls_item}>
          <label>{t("dealers.INN")}</label>
          <Input
            key={`INN`}
            control={control}
            name={"INN"}
            orange
            customClassName={s.item__little}
          />
        </li>
        <li className={s.filterControls_item}>
          <label>{t("dealers.status")}</label>
          <Select
            key={`status`}
            name={"status"}
            type={"dealersForm"}
            control={control}
            customClassName={s.item__little}
            options={["common.not_selected", ...DEALER_PASSPORT_OPTION_STATUS]}
          />
        </li>

        <li className={s.filterControls_item}>
          <label>{t("dealers.birCode")}</label>
          <Input
            key={`birCode`}
            control={control}
            name={"birCode"}
            orange
            customClassName={s.item__little}
          />
        </li>
      </ul>
      <ul className={s.filterControls}>
        <li className={s.filterControls_item}>
          <label>{t("dealers.country")}</label>
          <Select
            key={`country`}
            name={"country"}
            control={control}
            customClassName={s.item__little}
            options={optionLocationItem("country")}
          />
        </li>
        <li className={s.filterControls_item}>
          <label>{t("dealers.FO")}</label>
          <Select
            key={`FO`}
            name={"FO"}
            control={control}
            customClassName={s.item__little}
            options={optionLocationItem("FO")}
          />
        </li>
        <li className={s.filterControls_item}>
          <label>{t("dealers.subject")}</label>
          <Select
            key={`subject`}
            name={"subject"}
            control={control}
            customClassName={s.item__little}
            options={optionLocationItem("subject")}
          />
        </li>
        <li className={s.filterControls_item}>
          <label>{t("dealers.city")}</label>
          <Select
            key={`city`}
            name={"city"}
            control={control}
            customClassName={s.item__little}
            options={optionLocationItem("city")}
          />
        </li>

        <li className={s.filterControls_item}>
          <label>{t("dealers.category")}</label>
          <Input
            key={`category`}
            control={control}
            name={"category"}
            orange
            customClassName={s.item__little}
          />
        </li>
      </ul>

      <ul className={s.filterControls}>
        <li className={s.filterControls_item}>
          <label>{t("dealers.Main")}</label>
          <Select
            key={`Main`}
            name={"Main"}
            control={control}
            customClassName={s.item__little}
            optionObjects={[
              { label: "Ничего не выбрано", value: undefined },
              { label: "Да", value: 1 },
              { label: "Нет", value: 0 },
            ]}
          />
        </li>
        <li className={s.filterControls_item}>
          <label>{t("dealers.extendedForNextYear")}</label>
          <Select
            key={`extendedForNextYear`}
            name={"extendedForNextYear"}
            control={control}
            customClassName={s.item__little}
            optionObjects={[
              { label: "Ничего не выбрано", value: undefined },
              { label: "Да", value: 1 },
              { label: "Нет", value: 0 },
            ]}
          />
        </li>
      </ul>

      <div className={s.controlWrapper}>
        <Button
          customClassName={clsx(s.btn, s.btn_white)}
          txtBtn={t("ui.filter_reset")}
          onClick={cancelForm}
        />
        <Button customClassName={s.btn} isForm txtBtn={t("ui.filter_show")} />
      </div>
    </form>
  );
};

export default DealersFilter;
